import type {
    NativeAppStructure,
    NativeHomepageZone,
    NativeRouteDocument,
    NativeWebShell
} from '@/types/aggregation/native-app-data';
import type {
    AppData,
    FooterData,
    HeaderData,
    LogOutPopData,
    NavigationItem,
    Product,
    RegionInfo,
    ZonesTypes
} from '@/types/aggregation/common';
import type { Experiment } from '@/types/aggregation/home';
import type { StaticContentBodyItem } from '@/types/aggregation/static';
import type { Game } from '@/types/games';
import type { Zone } from '@/types/zones/zone';
import { adaptFooterLegal, adaptFooterLinkSections, extractSupportLink } from '@/logic/layout-adapter/adapt-footer';
import { adaptHeader } from '@/logic/layout-adapter/adapt-header';
import { adaptNativeGame } from '@/logic/game-adapter/adapt-native-game';
import { adaptNavigationItems, adaptTopNavigation } from '@/logic/page-config/navigation/navigation-adapter';
import type {
    AppPageViewModel,
    AppRouteKind,
    AppSeoViewModel,
    AppShellViewModel,
    NativeResolvedAppData,
    PageConfigResolvedAppData
} from './app-data.types';

type LegacyPageConfigResponse = {
    pageConfig?: {
        product?: Product;
        content?: {
            id?: string;
            uid?: string;
            type?: string;
            data?: Record<string, unknown>;
        };
        rewards_machine_content?: unknown;
        biggest_win_data?: Record<string, unknown>;
        categoryRankings?: {
            categories?: Array<{ category_name: string }>;
        } | null;
    };
    regionInfo?: RegionInfo;
    experiments?: Experiment;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapData = <T extends object>(value: T | { data?: T } | undefined): Partial<T> | undefined => {
    if (!value) {
        return undefined;
    }
    return ('data' in value && isRecord(value.data) ? value.data : value) as Partial<T>;
};

const getZones = (value: unknown): ZonesTypes[] => (Array.isArray(value) ? (value as ZonesTypes[]) : []);
const getBody = (value: unknown): StaticContentBodyItem[] =>
    Array.isArray(value) ? (value as StaticContentBodyItem[]) : [];

const adaptSeo = (data: Record<string, unknown>): AppSeoViewModel => ({
    title: typeof data.seo_meta_title === 'string' ? data.seo_meta_title : '',
    description: typeof data.seo_meta_description === 'string' ? data.seo_meta_description : '',
    headline:
        typeof data.homepage_description_headline === 'string'
            ? data.homepage_description_headline
            : typeof data.seo_category_description_headline === 'string'
              ? data.seo_category_description_headline
              : typeof data.seo_game_description_headline === 'string'
                ? data.seo_game_description_headline
                : '',
    richDescription:
        (data.homepage_description as AppSeoViewModel['richDescription']) ??
        (data.seo_game_description as AppSeoViewModel['richDescription']) ??
        (data.description as AppSeoViewModel['richDescription']) ??
        []
});

const createPage = (
    routeKind: AppRouteKind,
    uid: string,
    contentData: Record<string, unknown>,
    options: {
        routeData?: Record<string, unknown>;
        zones?: ZonesTypes[];
    } = {}
): AppPageViewModel => ({
    kind: routeKind,
    uid,
    seo: adaptSeo(contentData),
    zones: options.zones ?? getZones(contentData.zones),
    body: getBody(contentData.body),
    contentData,
    routeData: options.routeData
});

const toFeatureNames = (features: unknown): string[] => {
    if (!Array.isArray(features)) {
        return [];
    }
    return features
        .map((feature) => {
            if (typeof feature === 'string') {
                return feature;
            }
            return isRecord(feature) && typeof feature.feature_name === 'string' ? feature.feature_name : null;
        })
        .filter((feature): feature is string => feature !== null);
};

const createShell = ({
    header,
    footer,
    topNavigation,
    bottomNavigation,
    personalizedCategories,
    themeName,
    bonusDraw,
    productFeatures,
    siteWideJackpot,
    logoutPopup,
    app,
    rewardsMachineContent
}: {
    header?: Partial<HeaderData>;
    footer?: Partial<FooterData>;
    topNavigation?: NavigationItem[];
    bottomNavigation?: NavigationItem[];
    personalizedCategories?: string[];
    themeName?: string;
    bonusDraw?: string;
    productFeatures?: string[];
    siteWideJackpot?: AppShellViewModel['siteWideJackpot'];
    logoutPopup?: Partial<LogOutPopData>;
    app?: Partial<AppData>;
    rewardsMachineContent?: unknown;
}): AppShellViewModel => {
    const footerLinks = adaptFooterLinkSections(footer);

    return {
        header: adaptHeader(header),
        footerLinks,
        footerLegal: adaptFooterLegal(footer),
        supportLink: extractSupportLink(footerLinks),
        topNavigation: adaptTopNavigation(
            topNavigation ?? [],
            siteWideJackpot,
            (personalizedCategories?.length ?? 0) > 0,
            productFeatures,
            bottomNavigation
        ),
        bottomNavigation: bottomNavigation ? adaptNavigationItems(bottomNavigation) : null,
        themeName: themeName ?? 'default',
        bonusDraw: bonusDraw ?? '',
        productFeatures: productFeatures ?? [],
        siteWideJackpot: siteWideJackpot ?? ({} as AppShellViewModel['siteWideJackpot']),
        logoutPopup,
        app,
        rewardsMachineContent
    };
};

export const adaptPageConfigAppData = (
    route: string,
    routeKind: AppRouteKind,
    response: LegacyPageConfigResponse | undefined
): PageConfigResolvedAppData | null => {
    const pageConfig = response?.pageConfig;
    const content = pageConfig?.content;
    const productData = pageConfig?.product?.data;
    const contentData = content?.data ?? {};

    if (!response || !content || !productData) {
        return null;
    }

    const uid = content?.uid ?? route;
    return {
        source: 'page-config',
        route,
        routeKind,
        shell: createShell({
            header: productData?.header?.data,
            footer: productData?.footer?.data,
            topNavigation: productData?.top_navigation?.data?.items,
            bottomNavigation: productData?.bottom_bar?.data?.items,
            personalizedCategories: (pageConfig?.categoryRankings?.categories ?? []).map(
                (category) => category.category_name
            ),
            themeName: productData?.theme_name,
            bonusDraw: productData?.bonus_draw,
            productFeatures: toFeatureNames(productData?.features),
            siteWideJackpot: productData?.site_wide_jackpot,
            logoutPopup: productData?.logout_popup?.data,
            app: productData?.app?.data,
            rewardsMachineContent: pageConfig?.rewards_machine_content
        }),
        page: createPage(routeKind, uid, contentData, {
            routeData: pageConfig?.biggest_win_data
        }),
        regionInfo: response.regionInfo,
        experiments: response.experiments ?? {}
    };
};

export const isCompleteNativeWebShell = (structure: NativeWebShell | null): structure is NativeWebShell =>
    Boolean(
        structure &&
            structure.header &&
            structure.footer &&
            Array.isArray(structure.top_navigation?.items) &&
            Array.isArray(structure.bottom_bar?.items) &&
            Array.isArray(structure.product_features) &&
            structure.site_wide_jackpot &&
            structure.region_info
    );

export const isCompleteNativeAppStructure = (structure: NativeAppStructure | null): structure is NativeAppStructure =>
    Boolean(
        isCompleteNativeWebShell(structure) &&
            structure.homepage_content &&
            Array.isArray(structure.view) &&
            Array.isArray(structure.game_catalog)
    );

export const getMissingNativeAppDataFields = (
    routeKind: AppRouteKind,
    structure: NativeWebShell | null,
    routeDocument: NativeRouteDocument | null
): string[] => {
    if (!structure) {
        return [routeKind === 'home' ? 'appStructure' : 'webShell'];
    }

    const missing = [
        !structure.header && 'header',
        !structure.footer && 'footer',
        !Array.isArray(structure.top_navigation?.items) && 'top_navigation.items',
        !Array.isArray(structure.bottom_bar?.items) && 'bottom_bar.items',
        !Array.isArray(structure.product_features) && 'product_features',
        !structure.site_wide_jackpot && 'site_wide_jackpot',
        !structure.region_info && 'regionInfo'
    ].filter((field): field is string => Boolean(field));

    if (routeKind === 'home') {
        const appStructure = structure as NativeAppStructure;
        if (!appStructure.homepage_content) missing.push('homepage_content');
        if (!Array.isArray(appStructure.view)) missing.push('view');
        if (!Array.isArray(appStructure.game_catalog)) missing.push('game_catalog');
    } else if (routeKind === 'category' && !routeDocument?.uid) {
        missing.push('route_document');
    }

    return [...new Set(missing)];
};

const getUidFromUrn = (urn: string | undefined): string => urn?.split(':').at(-1) ?? '';

const adaptNativeZoneItems = (items: unknown[] | undefined, gamesByIdentifier: Map<string, Game>): unknown[] =>
    (items ?? []).flatMap((item) => {
        if (typeof item === 'string') {
            const game = gamesByIdentifier.get(item);
            if (game) {
                return [game];
            }
            return item.includes(':game:') ? [] : [item];
        }
        if (isRecord(item) && typeof item.item === 'string') {
            const game = gamesByIdentifier.get(item.item);
            return game ? [{ ...item, item: game }] : [];
        }
        return [item];
    });

const adaptNativeExclusiveZone = (zone: NativeHomepageZone, gamesByIdentifier: Map<string, Game>): Zone => {
    const uid = getUidFromUrn(zone.urn);
    const sliceType = zone.slice_type ?? '';
    const zoneData = isRecord(zone.zone_data) ? zone.zone_data : {};
    const {
        landscape,
        game,
        gameInfo: _gameInfo,
        video_trailer,
        launch_date,
        description,
        show_popup_at_login,
        show_module_in_lobby,
        promo_url,
        promo_code,
        is_reminder_set,
        exclusive_launch_type,
        ...portraitPrimary
    } = zoneData;
    const selectedGame = typeof game === 'string' ? gamesByIdentifier.get(game) : undefined;

    return {
        id: uid,
        uid,
        type: 'exclusive_launch_zone',
        data: {
            portrait: [{ id: `${uid}-portrait`, slice_type: sliceType, primary: portraitPrimary }],
            landscape: [
                {
                    id: `${uid}-landscape`,
                    slice_type: sliceType,
                    primary: isRecord(landscape) ? landscape : {}
                }
            ],
            category: zone.category?.category_uid ? { uid: zone.category.category_uid } : undefined,
            select_game: selectedGame,
            video_trailer,
            launch_date,
            description,
            show_popup_at_login,
            show_module_in_lobby,
            promo_url,
            promo_code,
            exclusive_launch_type
        },
        is_reminder_set
    } as unknown as Zone;
};

const adaptNativeHomepageZone = (zone: NativeHomepageZone, gamesByIdentifier: Map<string, Game>): Zone | null => {
    const uid = getUidFromUrn(zone.urn);
    if (zone.type === 'multi_zone') {
        const nestedZones = (zone.zones ?? [])
            .map((nestedZone) => adaptNativeHomepageZone(nestedZone, gamesByIdentifier))
            .filter((nestedZone): nestedZone is Zone => nestedZone !== null);

        return {
            id: uid,
            uid,
            type: 'multi_zone',
            icon: zone.icon,
            data: {
                title: zone.title ?? [],
                dynamic_zone: zone.dynamic_zone ?? false,
                zones: nestedZones
            }
        } as unknown as Zone;
    }

    const sliceType = zone.slice_type;
    if (!sliceType || sliceType === 'footer') {
        return null;
    }
    if (zone.type === 'exclusive_launch_zone') {
        return adaptNativeExclusiveZone(zone, gamesByIdentifier);
    }
    const categoryUid = zone.category?.category_uid;
    const primary = zone.web_primary ??
        zone.zone_data ?? {
            layout: zone.layout,
            category: categoryUid ? { uid: categoryUid } : undefined,
            banner_type: zone.banner_type,
            bonus: zone.bonus
        };
    const nestedZones = zone.zones
        ?.map((nestedZone) => adaptNativeHomepageZone(nestedZone, gamesByIdentifier))
        .filter((nestedZone): nestedZone is Zone => nestedZone !== null);

    return {
        id: uid,
        uid,
        type: nestedZones ? 'multi_zone' : 'zone',
        data: {
            title: zone.title ?? [],
            icon: zone.icon,
            dynamic_zone: zone.dynamic_zone ?? false,
            body: [
                {
                    ...zone,
                    slice_type: sliceType,
                    primary,
                    items: adaptNativeZoneItems(zone.items, gamesByIdentifier)
                }
            ],
            ...(nestedZones ? { zones: nestedZones } : {})
        }
    } as unknown as Zone;
};

const getGamesByIdentifier = (catalog: NativeAppStructure['game_catalog']): Map<string, Game> => {
    const gamesByIdentifier = new Map<string, Game>();

    for (const item of catalog ?? []) {
        const game = adaptNativeGame(item);
        gamesByIdentifier.set(item.urn, game);
        gamesByIdentifier.set(item.id, game);
    }

    return gamesByIdentifier;
};

const getNativeHomeZones = (structure: NativeAppStructure): ZonesTypes[] => {
    if (structure.homepage_content?.zones) {
        return structure.homepage_content.zones;
    }
    const gamesByIdentifier = getGamesByIdentifier(structure.game_catalog);
    return (structure.view ?? [])
        .map((zone) => adaptNativeHomepageZone(zone, gamesByIdentifier))
        .filter((zone): zone is Zone => zone !== null);
};

const adaptNativePage = (
    routeKind: AppRouteKind,
    structure: NativeWebShell,
    routeDocument: NativeRouteDocument | null
): AppPageViewModel | null => {
    if (routeKind === 'home') {
        const appStructure = structure as NativeAppStructure;
        const homepage = appStructure.homepage_content;
        if (!homepage) {
            return null;
        }
        const contentData = { ...homepage } as Record<string, unknown>;
        return createPage('home', homepage.uid ?? 'homepage', contentData, {
            zones: getNativeHomeZones(appStructure)
        });
    }

    if (routeKind !== 'category' || !routeDocument?.uid) {
        return null;
    }

    const contentData: Record<string, unknown> = {
        ...(isRecord(routeDocument.content) ? routeDocument.content : {}),
        seo_meta_title: routeDocument.seo_meta_title,
        seo_meta_description: routeDocument.seo_meta_description,
        homepage_description_headline: routeDocument.homepage_description_headline,
        homepage_description: routeDocument.homepage_description
    };
    const zones = routeDocument.zones ?? getZones(contentData.zones);

    return createPage('category', routeDocument.uid, contentData, {
        routeData: routeDocument.route_data,
        zones
    });
};

export const adaptNativeAppData = (
    route: string,
    routeKind: AppRouteKind,
    structure: NativeWebShell | null,
    routeDocument: NativeRouteDocument | null
): NativeResolvedAppData | null => {
    if (!structure) {
        return null;
    }
    if (
        (routeKind === 'home' && !isCompleteNativeAppStructure(structure as NativeAppStructure)) ||
        (routeKind === 'category' && !isCompleteNativeWebShell(structure)) ||
        (routeKind !== 'home' && routeKind !== 'category')
    ) {
        return null;
    }
    const page = adaptNativePage(routeKind, structure, routeDocument);
    if (!page) {
        return null;
    }

    return {
        source: 'native-api',
        route,
        routeKind,
        shell: createShell({
            header: unwrapData(structure.header),
            footer: unwrapData(structure.footer),
            topNavigation: structure.top_navigation?.items,
            bottomNavigation: structure.bottom_bar?.items,
            themeName: structure.theme_name,
            bonusDraw: structure.bonus_draw,
            productFeatures: structure.product_features,
            siteWideJackpot: structure.site_wide_jackpot,
            logoutPopup: unwrapData(structure.logout_popup),
            app: unwrapData(structure.app),
            rewardsMachineContent: structure.rewards_machine_content
        }),
        page,
        regionInfo: structure.region_info,
        experiments: structure.experiments ?? {}
    };
};
